/**
 * useShareTarget — native-only hook that receives content shared INTO the app
 * from the OS share sheet (gallery photo/video, a file, a link) and surfaces it
 * to the UI so the user can pick a chat to send it to.
 *
 * Native side: a custom Capacitor plugin "ShareTarget" (Android: ShareTargetPlugin.java;
 * iOS: ShareTarget.swift + a Share Extension) copies the shared items into an
 * app-sandbox path the WebView can serve and exposes them as
 *   { items: [{ path, name, mimeType, size }], text }
 * with two delivery channels — mirroring useNativeCallKit:
 *   - getPending(): one-shot pull, for COLD start (the share launched the app)
 *   - addListener("shareReceived"): event, for WARM start (app already running)
 *
 * This hook converts each item into a real browser `File` (fetch the
 * Capacitor.convertFileSrc URL → Blob → File) and emits EVENTS.SHARE_RECEIVED.
 * The globally-mounted ShareTargetModal listens and opens the chat picker.
 *
 * Web and Desktop are a no-op (the mount effect early-returns when not native),
 * so EVENTS.SHARE_RECEIVED never fires off-device.
 */

import { useEffect, useRef } from "react";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import { Capacitor, registerPlugin, PluginListenerHandle } from "@capacitor/core";
import emitter, { EVENTS } from "lib/emitter";
import useAuthUser from "./useAuthUser";

const LOG = "[ShareTarget]";

export interface ShareItem {
  /** Absolute native filesystem path (app sandbox), served to the WebView via
   *  Capacitor.convertFileSrc and fetched into a Blob. */
  path: string;
  name: string;
  mimeType: string;
  size?: number;
}

interface SharePayload {
  items: ShareItem[];
  text?: string;
}

interface ShareTargetPlugin {
  /** Returns and clears any buffered shared payload (cold start). */
  getPending(): Promise<SharePayload | null>;
  addListener(event: "shareReceived", cb: (payload: SharePayload) => void): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

const ShareTarget = registerPlugin<ShareTargetPlugin>("ShareTarget");
const isNative = Capacitor.isNativePlatform();

/** Surfaced to the UI via EVENTS.SHARE_RECEIVED — carries converted File objects. */
export interface ShareReceivedPayload {
  /** Content-derived dedup key (cold getPending + warm event may describe the same share). */
  shareId: string;
  files: File[];
  text?: string;
}

async function shareItemToFile(item: ShareItem): Promise<File | null> {
  // The native side copies each shared item into an app-sandbox path; the
  // Capacitor bridge serves that path at the same https://localhost origin as
  // the WebView (convertFileSrc), so this fetch is same-origin — no CORS, no
  // extra native plugin needed. If it ever fails we drop just this one item.
  try {
    const res = await fetch(Capacitor.convertFileSrc(item.path));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return new File([blob], item.name, { type: item.mimeType || blob.type });
  } catch (e) {
    console.error(LOG, "Failed to read shared item", item.name, e);
    return null;
  }
}

async function payloadToEvent(p: SharePayload | null): Promise<ShareReceivedPayload | null> {
  if (!p) return null;
  const files = (await Promise.all((p.items || []).map(shareItemToFile))).filter((f): f is File => f !== null);
  const text = p.text || undefined;
  if (files.length === 0 && !text) return null;
  const shareId = `${text ?? ""}|${(p.items || []).map((i) => `${i.name}:${i.size ?? 0}`).join(",")}`;
  return { shareId, files, text };
}

export default function useShareTarget() {
  const { user } = useAuthUser();
  const userRef = useRef(user);
  userRef.current = user;

  // StrictMode / fast-remount guard (mirrors useNativeCallKit.registeredRef).
  const initedRef = useRef(false);
  const handlesRef = useRef<PluginListenerHandle[]>([]);
  // Shares that arrived while logged out — all replayed after SET_USER.
  const pendingRef = useRef<ShareReceivedPayload[]>([]);
  // Last surfaced shareId + a timer that clears it. The dedup exists ONLY to
  // collapse the cold getPending() + warm shareReceived pair of a single OS
  // delivery (they fire within ms). It must NOT permanently suppress a genuine
  // re-share of identical content later, so we forget the id after a short window.
  const lastShareIdRef = useRef<string | null>(null);
  const dedupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const surface = (ev: ShareReceivedPayload | null) => {
    if (!ev) return;
    if (ev.shareId && ev.shareId === lastShareIdRef.current) return; // collapse cold+warm pair
    lastShareIdRef.current = ev.shareId;
    if (dedupTimerRef.current) clearTimeout(dedupTimerRef.current);
    dedupTimerRef.current = setTimeout(() => {
      lastShareIdRef.current = null;
    }, 4000);
    if (!userRef.current) {
      // Not logged in yet — hold (queue) and replay once authenticated.
      pendingRef.current.push(ev);
      return;
    }
    emitter.emit(EVENTS.SHARE_RECEIVED, ev);
  };

  useEffect(() => {
    if (!isNative || initedRef.current) return;
    initedRef.current = true;
    let cancelled = false;

    (async () => {
      // Warm path — register the listener first; Capacitor does not buffer
      // events fired before a JS handler attaches.
      try {
        const handle = await ShareTarget.addListener("shareReceived", (p) => {
          void payloadToEvent(p).then(surface);
        });
        if (cancelled) handle.remove();
        else handlesRef.current.push(handle);
      } catch (e) {
        console.warn(LOG, "addListener failed", e);
      }
      // Cold path — drain the one-shot buffer the share left behind on launch.
      try {
        const pending = await ShareTarget.getPending();
        surface(await payloadToEvent(pending));
      } catch (e) {
        console.warn(LOG, "getPending failed", e);
      }
    })();

    return () => {
      cancelled = true;
      handlesRef.current.forEach((h) => h.remove());
      handlesRef.current = [];
      ShareTarget.removeAllListeners().catch(() => {});
      if (dedupTimerRef.current) clearTimeout(dedupTimerRef.current);
      initedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replay a logged-out share after login. App.tsx already treats
  // EVENT_NAMES.SET_USER as the authoritative login signal on the same emitter.
  useEffect(() => {
    if (!isNative) return;
    const onSetUser = (data: any) => {
      if (data?.user && pendingRef.current.length) {
        pendingRef.current.forEach((ev) => emitter.emit(EVENTS.SHARE_RECEIVED, ev));
        pendingRef.current = [];
      }
    };
    emitter.on(EVENT_NAMES.SET_USER, onSetUser);
    return () => emitter.off(EVENT_NAMES.SET_USER, onSetUser);
  }, []);
}
